import React, {useState, useEffect, useMemo} from "react";
import {useFormik} from "formik";
import {Input, DatePicker, Tag, message, Select, Row, Col, Upload, Badge} from "antd";
import {
    EditOutlined,
    PlusOutlined,
    UploadOutlined,
    EyeOutlined,
    ReloadOutlined,
    WarningOutlined
} from "@ant-design/icons";
import moment from "moment";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {paymentContractFeeTypes} from "constants/constants";
import {FeeTypeCard} from "./components/FeeTypeCard";
import {CustomButton} from "components/_defaultComponents/CustomButton/CustomButton";
import {
    FormContainer,
    FormTitle,
    GeneralInfoSection,
    InfoRow,
    InfoLabel,
    FeeTypesContainer,
    ActionButtonsContainer,
    HeaderActions,
    NoteSection
} from "./SubCompanyPaymentContractForm.styles";
import {InformCloseCourierRequestModal} from "../../modals/informCloseCourierRequestModal/InformCloseCourierRequestModal";
import {ViewContractFileModal} from "components/_defaultComponents/SubCompanyPaymentContractsTable/ViewContractFileModal/ViewContractFileModal";
import {RecalculatePastShiftsModal} from "./components/RecalculatePastShiftsModal";

const DEFAULT_DAYS_ALL_TRUE = {
    "0": true,
    "1": true,
    "2": true,
    "3": true,
    "4": true,
    "5": true,
    "6": true
};

const DEFAULT_DAYS_ALL_FALSE = {
    "0": false,
    "1": false,
    "2": false,
    "3": false,
    "4": false,
    "5": false,
    "6": false
};

export const SubCompanyPaymentContractForm = ({
    activeSubCompanyPaymentContract,
    subCompanyId,
    onSuccess,
    isPool = false,
    isDedicated = false
}) => {
    const stores = useStore();
    const [isEditing, setIsEditing] = useState(false);
    const [showRecalcModal, setShowRecalcModal] = useState(false);
    const [feeItemsState, setFeeItemsState] = useState({});
    const [showInformCloseCourierRequestModal, setShowInformCloseCourierRequestModal] = useState(
        false
    );
    const [isSaving, setIsSaving] = useState(false);
    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileViewUrl, setFileViewUrl] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [fileViewLoading, setFileViewLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const userIsCustomer = !stores.meStore.checkRole("notVisibleForCompanyUser");

    const [
        activeCourierRequestDataLoading,
        activeCourierRequestData,
        ,
        ,
        refetchActiveCourierRequestData
    ] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierRequestStore.getActiveCourierRequestBySubCompanyId.bind(null, {
            subCompanyId
        }),
        dependencies: [subCompanyId],
        initialCall: true,
        condition: !!subCompanyId
    });

    const [
        latestContractFileLoading,
        latestContractFileData,
        ,
        ,
        refetchLatestContractFile
    ] = useFetch({
        defaultDataValue: null,
        fetchFn: stores.companyStore.getLatestSubCompanyPaymentContractFile.bind(null, {
            subCompanyId
        }),
        dependencies: [subCompanyId],
        initialCall: true,
        condition: !!subCompanyId && !userIsCustomer
    });

    useEffect(() => {
        // Düzenleme modundayken backend verisi değişse bile local state'i koruyoruz
        // Bu sayede kullanıcının yaptığı silme/değişiklikler kaybolmaz
        if (isEditing) {
            return;
        }

        if (activeSubCompanyPaymentContract?.feeItems) {
            organizeFeeItems(activeSubCompanyPaymentContract.feeItems);
        } else {
            initializeEmptyFeeItems();
        }
    }, [JSON.stringify(activeSubCompanyPaymentContract), isPool, isEditing]);

    const organizeFeeItems = feeItems => {
        const organized = {};

        const allowedFeeTypes = isPool
            ? paymentContractFeeTypes.filter(type => ["PAKET", "KM"].includes(type.value))
            : paymentContractFeeTypes;

        allowedFeeTypes.forEach(type => {
            const items = feeItems.filter(item => item.feeType === type.value);

            // Sadece bu fee type için item varsa ekle
            if (items.length > 0) {
                // BONUS_PAKET_KAZANCI_UCRETI için tüm itemlar custom olarak işlenir
                if (type.value === "BONUS_PAKET_KAZANCI_UCRETI") {
                    // Tüm itemları custom olarak işle (isCustomTimed=true ile)
                    const allItemsAsCustom = items.map(item => ({
                        ...item,
                        options: {...item.options, isCustomTimed: true}
                    }));
                    organized[type.value] = {
                        default: null,
                        custom:
                            allItemsAsCustom.length > 0
                                ? allItemsAsCustom
                                : [createCustomFeeItem(type.value)]
                    };
                } else {
                    const defaultItem = items.find(item => !item.options?.isCustomTimed);
                    const customItems = items.filter(item => item.options?.isCustomTimed);

                    organized[type.value] = {
                        default: defaultItem || createEmptyFeeItem(type.value),
                        custom: customItems
                    };
                }
            }
        });

        setFeeItemsState(organized);
    };

    const initializeEmptyFeeItems = () => {
        // Başlangıçta boş bir nesne - kullanıcı ihtiyaca göre ekleyecek
        setFeeItemsState({});
    };

    const createEmptyFeeItem = feeType => {
        // Sadece BONUS_PAKET_KAZANCI_UCRETI default bölümde günler gösterir
        const needsDaysInDefault = ["BONUS_PAKET_KAZANCI_UCRETI"].includes(feeType);

        // SAATLIK_GARANTI_PAKET için farklı alan yapısı (günler olmadan)
        if (feeType === "SAATLIK_GARANTI_PAKET") {
            return {
                feeType,
                clientGuaranteePackageCount: undefined,
                courierGuaranteePackageCount: undefined,
                options: {
                    feeTypesCompare: []
                }
            };
        }

        const baseItem = {
            feeType,
            clientAmount: undefined,
            courierAmount: undefined,
            courierMinimumDistance: feeType === "KM" ? 0 : null,
            clientMinimumDistance: feeType === "KM" ? 0 : null
        };

        if (needsDaysInDefault) {
            baseItem.options = {
                days: DEFAULT_DAYS_ALL_FALSE,
                minPackageCount: feeType === "BONUS_PAKET_KAZANCI_UCRETI" ? "" : undefined
            };
        }

        return baseItem;
    };

    const createCustomFeeItem = feeType => {
        // SAATLIK_GARANTI_PAKET için farklı alan yapısı
        if (feeType === "SAATLIK_GARANTI_PAKET") {
            return {
                feeType,
                clientGuaranteePackageCount: undefined,
                courierGuaranteePackageCount: undefined,
                options: {
                    isCustomTimed: true,
                    days: DEFAULT_DAYS_ALL_FALSE,
                    feeTypesCompare: []
                }
            };
        }

        // PAKET tipi için kademe yapısı (packageLimit alanı)
        if (feeType === "PAKET") {
            return {
                feeType,
                clientAmount: undefined,
                courierAmount: undefined,
                options: {
                    isCustomTimed: true,
                    days: DEFAULT_DAYS_ALL_FALSE,
                    packageLimit: undefined
                }
            };
        }

        return {
            feeType,
            clientAmount: undefined,
            courierAmount: undefined,
            courierMinimumDistance: feeType === "KM" ? 0 : null,
            clientMinimumDistance: feeType === "KM" ? 0 : null,
            options: {
                isCustomTimed: true,
                days: DEFAULT_DAYS_ALL_FALSE,
                minPackageCount: feeType === "BONUS_PAKET_KAZANCI_UCRETI" ? "" : undefined
            }
        };
    };

    const handleDefaultChange = (feeType, field, value) => {
        setFeeItemsState(prev => {
            const updated = {...prev};
            const fieldPath = field.split(".");

            if (fieldPath.length === 1) {
                updated[feeType].default[field] = value;
            } else {
                if (!updated[feeType].default.options) {
                    updated[feeType].default.options = {};
                }
                updated[feeType].default.options[fieldPath[1]] = value;
            }

            return updated;
        });
    };

    const handleCustomItemChange = (feeType, index, field, value) => {
        setFeeItemsState(prev => {
            const updated = {...prev};
            const fieldPath = field.split(".");

            if (fieldPath.length === 1) {
                updated[feeType].custom[index][field] = value;
            } else {
                if (!updated[feeType].custom[index].options) {
                    updated[feeType].custom[index].options = {};
                }
                updated[feeType].custom[index].options[fieldPath[1]] = value;
            }

            return updated;
        });
    };

    const handleAddCustomItem = feeType => {
        setFeeItemsState(prev => ({
            ...prev,
            [feeType]: {
                ...prev[feeType],
                custom: [...prev[feeType].custom, createCustomFeeItem(feeType)]
            }
        }));
    };

    const handleRemoveCustomItem = (feeType, index) => {
        setFeeItemsState(prev => ({
            ...prev,
            [feeType]: {
                ...prev[feeType],
                custom: prev[feeType].custom.filter((_, i) => i !== index)
            }
        }));
    };

    const handleAddFeeType = feeType => {
        if (!feeType || feeItemsState[feeType]) return;

        // Pool için sadece PAKET ve KM tipinde sözleşme eklenebilir
        if (isPool && !["PAKET", "KM"].includes(feeType)) {
            message.warning(
                "Havuz tipi alt firmalar için sadece Paket ve Mesafe tipinde sözleşme eklenebilir"
            );
            return;
        }

        // BONUS_PAKET_KAZANCI_UCRETI için default null, ilk satır custom olarak eklenir
        if (feeType === "BONUS_PAKET_KAZANCI_UCRETI") {
            setFeeItemsState(prev => ({
                ...prev,
                [feeType]: {
                    default: null,
                    custom: [createCustomFeeItem(feeType)]
                }
            }));
            return;
        }

        setFeeItemsState(prev => ({
            ...prev,
            [feeType]: {
                default: createEmptyFeeItem(feeType),
                custom: []
            }
        }));
    };

    const handleRemoveFeeType = feeType => {
        setFeeItemsState(prev => {
            const updated = {...prev};
            delete updated[feeType];
            return updated;
        });
    };

    const convertFeeItemsToPayload = () => {
        const feeItems = [];

        Object.entries(feeItemsState).forEach(([feeType, items]) => {
            // Pool için sadece PAKET ve KM gönderilir
            if (isPool && !["PAKET", "KM"].includes(feeType)) {
                return;
            }

            // SAATLIK_GARANTI_PAKET için farklı alan kontrolü
            const isPackageGuarantee = feeType === "SAATLIK_GARANTI_PAKET";

            // Add default item if has values
            const hasDefaultValues = isPackageGuarantee
                ? items.default?.clientGuaranteePackageCount !== undefined ||
                  items.default?.courierGuaranteePackageCount !== undefined
                : items.default?.clientAmount !== undefined ||
                  items.default?.courierAmount !== undefined;

            if (hasDefaultValues) {
                const defaultItem = {...items.default};

                // Don't send options for PAKET and KM if no custom times
                if (
                    ["PAKET", "KM"].includes(feeType) &&
                    (!defaultItem.options || !defaultItem.options.isCustomTimed)
                ) {
                    delete defaultItem.options;
                }

                feeItems.push(defaultItem);
            }

            // Add custom items
            items.custom?.forEach(item => {
                const hasCustomValues = isPackageGuarantee
                    ? item.clientGuaranteePackageCount !== undefined ||
                      item.courierGuaranteePackageCount !== undefined
                    : item.clientAmount !== undefined || item.courierAmount !== undefined;

                if (hasCustomValues) {
                    feeItems.push(item);
                }
            });
        });

        return feeItems;
    };

    const validateFeeItems = () => {
        // SAATLIK_GARANTI_PAKET varsa, PAKET tipi de olmalı
        const hasSaatlikGarantiPaket = feeItemsState["SAATLIK_GARANTI_PAKET"] !== undefined;
        const hasPaketType = feeItemsState["PAKET"] !== undefined;

        if (hasSaatlikGarantiPaket && !hasPaketType) {
            return {
                isValid: false,
                message:
                    "Saatlik Paket Garanti tanımlamak için önce Paket ücret tipi tanımlanmalıdır"
            };
        }

        // PAKET tipi varsa ve clientAmount veya courierAmount değeri yoksa validasyon hatası
        if (hasSaatlikGarantiPaket && hasPaketType) {
            const paketDefault = feeItemsState["PAKET"]?.default;
            if (!paketDefault?.clientAmount && !paketDefault?.courierAmount) {
                return {
                    isValid: false,
                    message: "Saatlik Paket Garanti tanımlamak için Paket ücreti girilmelidir"
                };
            }
        }

        return {isValid: true};
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const isPastDatedStartDate =
                formik.values.startDate &&
                moment(formik.values.startDate).isBefore(moment().startOf("day"));

            if (
                isDedicated &&
                isPastDatedStartDate &&
                !formik.values.description?.trim()
            ) {
                message.warning("Geçmiş tarihli sözleşme için açıklama girilmesi zorunludur");
                return;
            }

            const validation = validateFeeItems();
            if (!validation.isValid) {
                message.warning(validation.message);
                return;
            }

            const feeItems = convertFeeItemsToPayload();

            if (feeItems.length === 0) {
                message.warning("En az bir ücret tipi doldurulmalıdır");
                return;
            }

            const contract = {
                subCompanyId: subCompanyId || activeSubCompanyPaymentContract?.subCompanyId,
                description: formik.values.description || "",
                startDate: formik.values.startDate
                    ? moment(formik.values.startDate).format("YYYY-MM-DD")
                    : moment().format("YYYY-MM-DD"),
                feeItems
            };

            const result = await stores.companyStore.createSubCompanyPaymentContract({contract});

            if (result?.data?.success) {
                message.success("Sözleşme başarıyla kaydedildi");
                setIsEditing(false);
                onSuccess?.();
            } else {
                message.error(result?.data?.message || "Sözleşme kaydedilemedi");
            }
        } catch (error) {
            message.error(error.message || "Bir hata oluştu");
        } finally {
            setIsSaving(false);
        }
    };

    const handleFileUpload = async file => {
        if (!subCompanyId) {
            message.error("Alt firma bilgisi bulunamadı");
            return false;
        }

        const isValidType = ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(
            file.type
        );
        if (!isValidType) {
            message.error("Sadece PDF, JPG ve PNG dosyaları yüklenebilir");
            return false;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("subCompanyId", subCompanyId);

            const result = await stores.companyStore.uploadSubCompanyPaymentContractFile({
                file,
                subCompanyId
            });

            if (result?.success) {
                message.success("Sözleşme dosyası başarıyla yüklendi");
                refetchLatestContractFile();
            } else {
                message.error(result?.message || "Dosya yüklenirken bir hata oluştu");
            }
        } catch (error) {
            message.error(error.message || "Dosya yüklenirken bir hata oluştu");
        } finally {
            setIsUploading(false);
        }

        return false;
    };

    const handleViewFile = async () => {
        if (!latestContractFileData?.paymentContractFile?._id) {
            message.warning("Görüntülenecek dosya bulunamadı");
            return;
        }
        const paymentContractFile = latestContractFileData?.paymentContractFile;

        try {
            setShowFileViewModal(true);
            setFileViewLoading(true);

            const result = await stores.companyStore.downloadSubCompanyPaymentContractFile({
                paymentContractFileId: paymentContractFile._id
            });

            if (result?.file?.data && Array.isArray(result.file.data)) {
                const byteArray = new Uint8Array(result.file.data);
                const blob = new Blob([byteArray], {type: result.mimeType});
                const blobUrl = URL.createObjectURL(blob);

                setFileViewUrl(blobUrl);
                setDocumentType(result.mimeType);
                setFileViewLoading(false);
                return;
            }

            message.error("Dosya bulunamadı");
            setShowFileViewModal(false);
            setFileViewLoading(false);
        } catch (error) {
            message.error(error.message || "Dosya görüntülenirken bir hata oluştu");
            setShowFileViewModal(false);
            setFileViewLoading(false);
        }

        /////////
    };

    const handleCloseFileViewModal = () => {
        setShowFileViewModal(false);
        setFileViewUrl(null);
        setDocumentType(null);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        if (activeSubCompanyPaymentContract?.feeItems) {
            organizeFeeItems(activeSubCompanyPaymentContract.feeItems);
        } else {
            initializeEmptyFeeItems();
        }
        formik.resetForm();
    };

    // Sözleşme yoksa varsayılan başlangıç tarihi - useMemo ile stabil tutulur
    const defaultStartDate = useMemo(() => moment(), []);

    const formik = useFormik({
        initialValues: {
            description: activeSubCompanyPaymentContract?.description || "",
            startDate: activeSubCompanyPaymentContract?.startDate
                ? moment(activeSubCompanyPaymentContract.startDate)
                : defaultStartDate
        },
        enableReinitialize: true
    });

    const isPastDatedStartDate =
        formik.values.startDate &&
        moment(formik.values.startDate).isBefore(moment().startOf("day"));

    const canManageContract = stores.meStore.checkRole("createSubCompanyPaymentContract");
    const showRecalcButton =
        isDedicated &&
        canManageContract &&
        activeSubCompanyPaymentContract?.hasOldCalculatedShifts;

    return (
        <>
            <FormContainer>
                <Row
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start"
                    }}
                >
                    <Col>
                        <FormTitle>
                            Güncel Sözleşme
                            {activeSubCompanyPaymentContract?.contractId && (
                                <Tag style={{marginLeft: 12, fontWeight: "normal"}}>
                                    {activeSubCompanyPaymentContract.contractId}
                                </Tag>
                            )}
                        </FormTitle>

                        <GeneralInfoSection>
                            <InfoRow>
                                <InfoLabel>Başlangıç Tarihi:</InfoLabel>
                                <DatePicker
                                    className="payment-contract-form-input"
                                    locale={locale}
                                    value={formik.values.startDate}
                                    format="DD.MM.YYYY"
                                    disabledDate={current =>
                                        isDedicated
                                            ? current &&
                                              current < moment().subtract(3, "months").startOf("day")
                                            : current && current <= moment().subtract(1, "day")
                                    }
                                    onChange={date => formik.setFieldValue("startDate", date)}
                                    disabled={!isEditing}
                                    style={{width: 250, maxWidth: "100%"}}
                                    allowClear={false}
                                />
                            </InfoRow>

                            <InfoRow>
                                <InfoLabel>
                                    Açıklama:
                                    {isDedicated && isPastDatedStartDate && (
                                        <span style={{color: "#ff4d4f", marginLeft: 4}}>*</span>
                                    )}
                                </InfoLabel>
                                <Input
                                    className="payment-contract-form-input"
                                    value={formik.values.description}
                                    onChange={e =>
                                        formik.setFieldValue("description", e.target.value)
                                    }
                                    disabled={!isEditing}
                                    style={{width: 350, maxWidth: "100%"}}
                                    placeholder="Sözleşme açıklaması"
                                />
                            </InfoRow>
                        </GeneralInfoSection>
                    </Col>

                    <Col>
                        <HeaderActions>
                            {showRecalcButton && (
                                <Badge
                                    count={
                                        activeSubCompanyPaymentContract?.oldCalculatedShiftCount || 0
                                    }
                                    offset={[-6, 2]}
                                >
                                    <CustomButton
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={() => setShowRecalcModal(true)}
                                    >
                                        Geçmiş Vardiyaları Tekrar Hesapla
                                    </CustomButton>
                                </Badge>
                            )}
                            {stores.meStore.checkRole("createSubCompanyPaymentContract") && (
                                <Upload
                                    beforeUpload={handleFileUpload}
                                    showUploadList={false}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                >
                                    <CustomButton
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        loading={isUploading}
                                    >
                                        Sözleşme Yükle
                                    </CustomButton>
                                </Upload>
                            )}
                        </HeaderActions>
                        {latestContractFileData?.paymentContractFile && (
                            <div style={{textAlign: "right", marginTop: 4}}>
                                <CustomButton
                                    type="link"
                                    icon={<EyeOutlined />}
                                    onClick={handleViewFile}
                                    loading={latestContractFileLoading}
                                    style={{padding: 0}}
                                >
                                    Görüntüle
                                </CustomButton>
                            </div>
                        )}
                    </Col>
                </Row>
                {showRecalcButton && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            marginBottom: 16,
                            color: "#8c8c8c"
                        }}
                    >
                        <WarningOutlined style={{color: "#faad14", marginTop: 3}} />
                        <span>
                            Geçmişe yönelik vardiyalarda yeniden hesaplanması gereken kayıtlar
                            mevcuttur. Geçmiş vardiyaları tekrar hesapla butonuna basarak işlemi
                            gerçekleştirebilirsiniz.
                        </span>
                    </div>
                )}
                <FeeTypesContainer>
                    {/* Sıralama: PAKET -> SAATLIK_UCRET -> KM -> SAATLIK_GARANTI_PAKET -> BONUS_PAKET */}
                    {paymentContractFeeTypes
                        .filter(ft => feeItemsState[ft.value] !== undefined)
                        .map(feeTypeInfo => {
                            const feeTypeValue = feeTypeInfo.value;

                            // Pool için sadece PAKET ve KM gösterilir
                            if (isPool && !["PAKET", "KM"].includes(feeTypeValue)) {
                                return null;
                            }

                            // SAATLIK_GARANTI_PAKET tooltip hesaplaması için PAKET fiyat bilgisi
                            const packagePriceInfo =
                                feeTypeValue === "SAATLIK_GARANTI_PAKET" && feeItemsState["PAKET"]
                                    ? {
                                          clientAmount:
                                              feeItemsState["PAKET"]?.default?.clientAmount || 0,
                                          courierAmount:
                                              feeItemsState["PAKET"]?.default?.courierAmount || 0
                                      }
                                    : null;

                            return (
                                <FeeTypeCard
                                    key={feeTypeValue}
                                    feeType={feeTypeValue}
                                    feeTypeName={feeTypeInfo.name}
                                    defaultFeeItem={feeItemsState[feeTypeValue]?.default}
                                    customFeeItems={feeItemsState[feeTypeValue]?.custom || []}
                                    disabled={!isEditing}
                                    onDefaultChange={handleDefaultChange}
                                    onCustomItemChange={handleCustomItemChange}
                                    onAddCustomItem={handleAddCustomItem}
                                    onRemoveCustomItem={handleRemoveCustomItem}
                                    onRemoveFeeType={handleRemoveFeeType}
                                    userIsCustomer={userIsCustomer}
                                    isPool={isPool}
                                    packagePriceInfo={packagePriceInfo}
                                    isEditing={isEditing}
                                />
                            );
                        })}

                    {isEditing &&
                        (() => {
                            const availableFeeTypes = (isPool
                                ? paymentContractFeeTypes.filter(type =>
                                      ["PAKET", "KM"].includes(type.value)
                                  )
                                : paymentContractFeeTypes
                            ).filter(feeType => !feeItemsState[feeType.value]);

                            return availableFeeTypes.length > 0 ? (
                                <div style={{marginTop: 10}}>
                                    <Select
                                        className="payment-contract-form-input"
                                        placeholder="Ücret Tipi Ekle"
                                        style={{width: 300}}
                                        onChange={handleAddFeeType}
                                        value={null}
                                        suffixIcon={<PlusOutlined />}
                                    >
                                        {availableFeeTypes.map(feeType => (
                                            <Select.Option
                                                key={feeType.value}
                                                value={feeType.value}
                                            >
                                                {feeType.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            ) : null;
                        })()}
                </FeeTypesContainer>
                {stores.meStore.checkRole("createSubCompanyPaymentContract") ? (
                    <ActionButtonsContainer>
                        {!isEditing ? (
                            <CustomButton
                                onClick={handleEdit}
                                icon={<EditOutlined />}
                                style={{minWidth: 120, fontWeight: 600}}
                            >
                                Düzenle
                            </CustomButton>
                        ) : (
                            <>
                                <CustomButton
                                    disabled={activeCourierRequestDataLoading}
                                    onClick={() => {
                                        //Aktif talep kaydı varsa, kurye talebi kapatılması konusunda bilgilendirme modalı gösterilir.
                                        if (activeCourierRequestData?.data) {
                                            setShowInformCloseCourierRequestModal(true);
                                        } else {
                                            handleSave();
                                        }
                                    }}
                                    type="primary"
                                    style={{minWidth: 120, fontWeight: 600}}
                                    loading={isSaving}
                                >
                                    Kaydet
                                </CustomButton>
                                <CustomButton
                                    onClick={handleCancel}
                                    style={{minWidth: 120, fontWeight: 600}}
                                    loading={isSaving}
                                >
                                    İptal
                                </CustomButton>
                            </>
                        )}
                    </ActionButtonsContainer>
                ) : (
                    <></>
                )}
                <NoteSection>
                    <div>* Müşteri Ücretleri KDV dahil değildir</div>
                    <div>* Kurye Ücretleri KDV dahildir</div>
                </NoteSection>
            </FormContainer>
            <InformCloseCourierRequestModal
                visibility={showInformCloseCourierRequestModal}
                onCancel={() => setShowInformCloseCourierRequestModal(false)}
                onOkFunc={async () => {
                    await handleSave();
                    setShowInformCloseCourierRequestModal(false);
                    await refetchActiveCourierRequestData();
                }}
            />

            <ViewContractFileModal
                visible={showFileViewModal}
                onCancel={handleCloseFileViewModal}
                documentUrl={fileViewUrl}
                documentType={documentType}
                loading={fileViewLoading}
            />

            <RecalculatePastShiftsModal
                visible={showRecalcModal}
                onCancel={() => setShowRecalcModal(false)}
                contractId={activeSubCompanyPaymentContract?._id}
                onSuccess={onSuccess}
            />
        </>
    );
};
